// src/Login.js

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import { supabase } from './supabaseClient';
import PublicHeader from './components/PublicHeader';

// This function should be defined globally or imported.
// Duplicating here for now. Consider refactoring to a shared utils/api.js file.
async function processInvitationOnBackend(token, userAccessToken) {
  console.log('[Login.js] Calling /process-invitation backend with token:', token);
  const response = await fetch('/functions/v1/process-invitation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userAccessToken}`,
      // 'apikey': 'YOUR_SUPABASE_ANON_KEY' // If needed
    },
    body: JSON.stringify({ invitation_token: token })
  });
  const responseBody = await response.json();
  if (!response.ok) {
    console.error('[Login.js] Backend process-invitation error:', responseBody);
    throw new Error(responseBody.details || responseBody.error || 'Failed to process invitation after login');
  }
  console.log('[Login.js] Backend process-invitation success after login:', responseBody);
  return responseBody;
}

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      // Successful login. Invitation processing is now handled by AuthContext when the session becomes active.
      // Login.js no longer needs to call processInvitationOnBackend directly.
      if (data.session) {
        // AuthContext will check for pendingInvitationToken from localStorage and process it.
        console.log('[Login.js] Login successful, session available. AuthContext will handle any pending invitation processing.');
      } else {
        // This case should ideally not happen if signInWithPassword was successful and returned a session.
        console.warn('[Login.js] Login reported success but no session data found immediately.');
      }
      navigate('/');
    } catch (err) {
      console.error('Error during login or post-login processing:', err.message);
      setError(err.message);
    }
  };

  const handleGoogleLogin = async () => {
    console.log('Google login function called');
    // Store token before OAuth redirect if it's available from URL (e.g. if user lands on /login?invitation_token=...)
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('invitation_token');
    if (tokenFromUrl) {
      localStorage.setItem('pendingInvitationToken', tokenFromUrl);
      console.log('[Login.js] Storing invitation token from URL before Google OAuth.');
    }
    try {
      console.log('Initiating Google OAuth sign-in');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin, // OAuth will redirect back, App.js/useEffect handles session and then invitation
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account' // Show account selection without forcing re-consent
          }
        }
      });
      if (error) {
        console.error('OAuth error:', error);
        throw error;
      }
    } catch (err) {
      console.error('Error with Google sign-in:', err.message);
      setError(err.message);
    }
  };

  return (
    <Box>
      <PublicHeader />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component="h1" variant="h5">
            Login
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Login
            </Button>
          </Box>

          <Button
            onClick={(e) => {
              console.log('Google login button clicked');
              handleGoogleLogin();
            }}
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Login with Google
          </Button>

          <Grid container>
            <Grid item xs>
              <Link to="/forgot-password" variant="body2">
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              <Link to="/signup" variant="body2">
                {"Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default Login;
